<template>
  <div class="new-ponderation-wrapper">
    <div class="ponderation-columns-wrapper">
      <div v-for="column in ponderationColumns" :key="column.id" class="ponderation-column-container">
        <div v-tooltip="column.tooltip" class="column-title">{{ $t(column.title) }}</div>
        <Draggable :disabled="column.id === 'ignored' && disableIgnored" class="column-skill-list" v-model="column.items" group="tasks" item-key="id">
          <template #item="{ element }">
            <div v-tooltip="isDisabledInEssentials(element) ? $t('app.campaign.newPage.notAvailableEssential') : null" class="skill" :class="{style: skills.find(s => (s.id === element || s.id_v1 === element || s.id_v2 === element) && s.type === 'style'), 'disabled-in-essential': isDisabledInEssentials(element)}">{{ skillLocale(element) }}</div>
          </template>
        </Draggable>
      </div>
    </div>
    <div class="legend">
      <span class="bold text">{{ $t('app.campaign.ponderation.legend.label') }}</span>
      <div class="score-dot"></div>
      <span class="">{{ $t('app.campaign.ponderation.legend.score') }}</span>
      <div class="style-dot"></div>
      <span class="">{{ $t('app.campaign.ponderation.legend.style') }}</span>
    </div>
  </div>
</template>

<style scoped lang="scss">
  @import "@/assets/style/variables.scss";

  .legend {
    flex-shrink: 0;
    flex-grow: 0;
    display: flex;
    align-items: center;
    gap: 8px;

    .text {
      margin-top: -4px;
    }

    .score-dot {
      margin-left: 6px;
      width: 10px;
      height: 10px;
      border-radius: 1px;
      background: #1A84CF;
    }

    .style-dot {
      margin-left: 6px;
      width: 10px;
      height: 10px;
      border-radius: 1px;
      background: #9E64E9;
    }
  }

  .new-ponderation-wrapper {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .ponderation-columns-wrapper {
    display: flex;
    gap: 16px;
    align-items: center;
    flex-shrink: 1;
    flex-grow: 1;
    height: 90%;
    width: 100%;
  }

  .ponderation-column-container {
    display: flex;
    padding: 24px;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    flex: 1 0 0;
    align-self: stretch;

    border-radius: 12px;
    border: 2px dashed $grey-50-color;
    background-color: var(--light-white-color);

    .column-title {
      padding: 8px 16px;

      background-color: $primary-color;
      border-radius: 4px;

      color: $secondary-color;
      text-align: center;
      font-family: $bioSans;
      font-size: 15px;
      font-style: normal;
      font-weight: 700;
      line-height: normal;
      text-transform: uppercase;
    }

    &:last-child {
      .column-skill-list {
        .skill {
          background: $disabled-background;
          color: $disabled-color;
          border-color: $disabled-color !important;
        }
      }
    }
    
    .column-skill-list {
      display: flex;
      align-items: flex-start;
      align-content: flex-start;
      gap: 8px;
      align-self: stretch;
      flex-wrap: wrap;
      overflow-y: auto;
      flex-basis: 100%;

      &::-webkit-scrollbar {
        width: 3px;
        border-radius: 24px;
        background-color: var(--light-grey-color);
      }

      &::-webkit-scrollbar-thumb {
        background: $primary-color;
        padding: 4px;
        width: 3px;
        border-radius: 24px;
      }

      .skill {
        padding: 8px 10px;

        border-radius: 8px;
        border: 2px solid #1A84CF;
        background: var(--white-color);

        color: var(--secondary-color);
        font-family: $bioSans;
        font-size: 14px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
        text-align: center;

        cursor: grab;
        user-select: none;

        /* While dragging */
        &.sortable-chosen {
          cursor: grabbing !important;
        }

        /* Item being dragged */
        &.sortable-drag {
          cursor: grabbing !important;
        }

        &.style {
          border: 2px solid #9E64E9;
        }

        &.disabled-in-essential {
          background: $disabled-background;
          color: $disabled-color;
          border-color: $disabled-color !important;
        }
      }
    }
  }
</style>

<script lang="ts" setup>
import { Campaign, CampaignCreation, WeightingModule } from '@/types/campaign';
import { useSkillsStore } from '@/stores/skills';
import Draggable from 'vuedraggable';

const { skills, isSkillCompatible, isCampaignV2, isCampaignV1, isCampaignV3OrMore } = useSkillsStore();
const { te, t } = useI18n();

const ponderationColumns = ref([]);

const props = defineProps<{
  modelValue: CampaignCreation,
  disableIgnored?: boolean,
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: CampaignCreation): void
}>();

function skillLocale(internal_name: string) {
  if (te(`skills.${internal_name}.name`)) {
    return t(`skills.${internal_name}.name`);
  }
  const skillInfo = skills.find(v => v.id === internal_name);
  if (te(`skills.${skillInfo?.id}.name`)) {
  } else if (te(`skills.${skillInfo?.id_v2}.name`)) {
    return t(`skills.${skillInfo?.id_v2}.name`);
  } else {
    return t(`skills.${skillInfo?.id_v1}.name`);
  }
}

function isDisabledInEssentials(id: string) {
  const skill = skills.find(s => s.id === id || s.id_v2 === id || s.id_v1 === id);
  const isDisabled = skill?.isAvailaibleInEssential === false && isCampaignV2(props.modelValue) && props.modelValue.variant === 'essentials';
  return isDisabled;
}

const requiredSkills = computed(() => props?.modelValue?.assessment?.weighting === 'neutral' ? skills.reduce((acc, s) => {
  if (!s.disabled && !s.incompatible_versions.includes(props.modelValue.campaign_version)) acc.push(s.id);
  return acc;
}, []) : ([...props?.modelValue?.assessment?.weighting?.required as string[]]));

const primarySkills = computed(() => {
  if (props?.modelValue?.assessment?.weighting === 'neutral') return [];
  let primary = [...props?.modelValue?.assessment?.weighting?.primary];

  if (isCampaignV3OrMore(props.modelValue)) {
    skills.filter(s => s.type === "style" && !s.disabled).forEach((skill) => {
      if (props?.modelValue?.assessment?.modules?.includes(skill.id as WeightingModule) && !primary.includes(skill.id as WeightingModule)) {
        primary.push(skill.id as WeightingModule);
      }
    });
  }

  return primary;
});

const secondarySkills = computed(() => {
  if (props?.modelValue?.assessment?.weighting === 'neutral') return [];
  return [...props?.modelValue?.assessment?.weighting?.secondary];
});

const ignoredSkills = computed(() => {
  if (props?.modelValue?.assessment?.weighting === 'neutral') return [];
  let ignored = [...props?.modelValue?.assessment?.weighting?.ignored];
  if (!isSkillCompatible(props.modelValue.campaign_version, 'self_efficacy')) {
    ignored = ignored.filter((v) => v !== 'self_efficacy');
  }
  if (isCampaignV3OrMore(props.modelValue)) {
    skills.filter(s => s.type === "style" && !s.disabled).forEach((skill) => {
      if (!props?.modelValue?.assessment?.modules?.includes(skill.id as WeightingModule) && !ignored.includes(skill.id as WeightingModule)) {
        ignored.push(skill.id as WeightingModule);
      }
    });
  }

  return ignored;
});

watch(ponderationColumns, () => {
  if (isCampaignV3OrMore(props.modelValue)) {
    if (typeof props.modelValue.assessment.weighting !== 'string') {
      props.modelValue.assessment.weighting.required = ponderationColumns.value[0].items.filter((item => skills.filter(s => s.type === 'style').map(s => s.id).includes(item) === false));
      props.modelValue.assessment.weighting.primary = ponderationColumns.value[1].items.filter((item => skills.filter(s => s.type === 'style').map(s => s.id).includes(item) === false));
      props.modelValue.assessment.weighting.secondary = ponderationColumns.value[2].items.filter((item => skills.filter(s => s.type === 'style').map(s => s.id).includes(item) === false));
      props.modelValue.assessment.weighting.ignored = ponderationColumns.value[3].items.filter((item => skills.filter(s => s.type === 'style').map(s => s.id).includes(item) === false));
    }
    ponderationColumns.value[0].items.concat(ponderationColumns.value[1].items).concat(ponderationColumns.value[2].items).concat(ponderationColumns.value[3].items).forEach((skillId) => {
      if (!props.modelValue.assessment.modules.includes(skillId as WeightingModule)) {
        props.modelValue.assessment.modules.push(skillId as WeightingModule);
      }
    });
    const ignoredStyles = ponderationColumns.value[3].items.filter((item => skills.map(s => s.id).includes(item)));
    props.modelValue.assessment.modules = props.modelValue.assessment.modules.filter((m) => !ignoredStyles.includes(m as WeightingModule));
  } else {
    if (typeof props.modelValue.assessment.weighting === 'string') {
      props.modelValue.assessment.weighting = {
        required: [],
        primary: [],
        secondary: [],
        ignored: [],
      };
    }
    props.modelValue.assessment.weighting.required = ponderationColumns.value[0].items;
    props.modelValue.assessment.weighting.primary = ponderationColumns.value[1].items;
    props.modelValue.assessment.weighting.secondary = ponderationColumns.value[2].items;
    props.modelValue.assessment.weighting.ignored = ponderationColumns.value[3].items;
  }
}, {deep: true});

onBeforeMount(() => {
  ponderationColumns.value = [
    { id: "required", title: "app.campaign.ponderation.mandatory", tooltip: "Coefficient : 3", items: requiredSkills.value },
    { id: "primary", title: "app.campaign.ponderation.important", tooltip: "Coefficient : 2", items: primarySkills.value },
    { id: "secondary", title: "app.campaign.ponderation.secondary", tooltip: "Coefficient : 1", items: secondarySkills.value },
    { id: "ignored", title: "app.campaign.ponderation.excluded", items: ignoredSkills.value },
  ];
});

</script>